import { Header } from '@components/Header';
import Add from '@components/icons/Add.svg';
import MoveIcon from '@components/icons/Move.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { Tag } from '@components/core-ui/Tag';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useNavigate } from 'react-router';

import { usePageContext } from '../../providers/PageProvider';
import { JobsTable } from './components/JobsTable';
import { StatusFilters } from './components/StatusFilters';
import { TabSwitcher } from './components/TabSwitcher';
import { WSUpdateReceiver } from './components/WSUpdateReceiver';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const {
    reload,
    currentTab,
    setCurrentTab,
    checkedJobs,
    dateFilter,
    setDateFilter,
    search,
    setSearch,
    jobs,
    setJobs,
    isLoading,
    errorMessage,
    isRetryDisabled,
    retry,
    exportCSV,
    isExportDisabled,
    isExporting,
    statusFilters,
    setStatusFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    orderBy,
    orderType,
    toggleSort,
    setCheckedJobs,
  } = usePageContext();
  const { openModal, closeModal } = useModalContext();
  const { organization, workspace } = useAuthContext();
  const navigate = useNavigate();

  const addJob = () => {
    navigate(routes.newJob.make(organization?.slug ?? '', workspace?.slug ?? ''));
  };

  const bulkAddJobs = () => {
    openModal(ModalNamesEnum.BulkJobCreation, {
      onSuccess: () => {
        reload();
        closeModal();
      },
    });
  };

  const canSubmitJobs = Boolean(organization?.permissions?.submitJobs);
  const primaryAction = canSubmitJobs
    ? { key: 'create', label: 'Create New Job', icon: Add, onClick: addJob, testId: 'create_job_header_button' }
    : undefined;
  const secondaryActions = canSubmitJobs
    ? [{ key: 'bulk', label: 'Bulk Create Jobs', icon: Add, onClick: bulkAddJobs }]
    : [];

  const showMoveJobsToWSModal = () => {
    openModal(ModalNamesEnum.MoveJobsToWSModal, {
      jobIds: checkedJobs,
      onSuccess: () => {
        reload();
        closeModal();
      },
    });
  };

  const showRemoveJobsModal = () => {
    openModal(ModalNamesEnum.DeleteJobsModal, {
      jobIds: checkedJobs,
      onSuccess: () => {
        reload();
        closeModal();
      },
    });
  };

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.JobsListFilters, {
      dateFilter,
      setDateFilter,
    });
  };

  if (!jobs) {
    return null;
  }

  return (
    <>
      <Header
        title="Jobs"
        setSearch={setSearch}
        search={search}
        primaryAction={primaryAction}
        actions={secondaryActions}
        buttonLabel={jobs.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={jobs.data?.length ? () => exportCSV() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={isExporting}
        leftSideContent={[<TabSwitcher key="TabSwitcher" currentTab={currentTab} setCurrentTab={setCurrentTab} />]}
        onFilterClick={openFiltersModal}
        filterCounter={dateFilter.from || dateFilter.to ? 1 : 0}
      />
      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        {errorMessage ? (
          <PageErrorState
            message={errorMessage}
            actionLabel="Retry"
            actionDisabled={isRetryDisabled}
            onAction={retry}
          />
        ) : null}
        <div className="flex flex-wrap gap-3 items-center">
          <StatusFilters statusFilters={statusFilters} setStatusFilters={setStatusFilters} />
          {checkedJobs.length > 0 && (
            <div className="selected-wrapper">
              <Tag
                className="h-[22px] m-0 mr-[5px] box-border"
                style={{
                  borderColor: 'var(--color-divider)',
                  background: 'var(--color-divider)',
                  color: 'var(--color-title)',
                }}
              >
                {checkedJobs.length} selected
              </Tag>

              {organization?.permissions?.submitJobs ? (
                <IconButton title="Move to another Workspace" onClick={() => showMoveJobsToWSModal()}>
                  <MoveIcon />
                </IconButton>
              ) : null}
              {organization?.permissions?.deleteJobs ? (
                <IconButtonDelete title="Remove" onClick={() => showRemoveJobsModal()}>
                  <TrashBin />
                </IconButtonDelete>
              ) : null}
            </div>
          )}
        </div>

        <JobsTable
          jobs={jobs}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          orderBy={orderBy}
          orderType={orderType}
          toggleSort={toggleSort}
          reload={reload}
          search={search}
          setSearch={setSearch}
          checkedJobs={checkedJobs}
          setCheckedJobs={setCheckedJobs}
        />
      </Wrapper>

      <WSUpdateReceiver
        jobs={jobs}
        setJobs={setJobs}
        organizationId={organization?.id ?? ''}
        workspaceId={workspace?.id ?? ''}
        reload={reload}
      />
    </>
  );
};
